import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';
import { Switch } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import { ConnectStripeButton } from '../components/ConnectStripeButton';
import ErrorBoundary from '../components/ErrorBoundary';
import ErrorPage from '../components/ErrorPage';
import Loader from '../components/Loader';
import NoSubscriptionPage from '../components/NoSubscriptionPage';
import PageHeader from '../components/PageHeader';
import PageWrapper from '../components/PageWrapper';
import Sidebar from '../components/Sidebar';
import { CurrentPlanContext } from '../hooks/CurrenPlanContext';
import { CurrentCompanyContext } from '../hooks/CurrentCompanyContext';
import { useConnectStripeModal } from '../hooks/useConnectStripeModal';
import { useUpgradeModal } from '../hooks/useUpgradeModal';
import Billing from '../pages/Billing';
import Coupons from '../pages/Coupons';
import CustomerPortals from '../pages/CustomerPortals';
import NewCustomerPortal from '../pages/CustomerPortals/NewCustomerPortal';
import FeatureFlags from '../pages/FeatureFlags';
import ConfigureFeatureFlags from '../pages/FeatureFlags/ConfigureFeatureFlags';
import GatedContent from '../pages/GatedContent';
import NewGatedContent from '../pages/GatedContent/NewGatedContent';
import HelpPage from '../pages/HelpPage';
import HomePage from '../pages/Home';
import Integrations from '../pages/Integrations';
import InvoiceGenerator from '../pages/InvoiceGenerator';
import NotFound from '../pages/NotFound';
import PricingPages from '../pages/PricingPages';
import NewPage from '../pages/PricingPages/NewPage/index';
import Settings from '../pages/Settings';
import Company from '../types/company';
import { useAxios } from '../utils/hooks';
import { PAID_PLANS, PRO_PLANS, UserPlan } from '../utils/plans';
import { ROUTES } from './routes';
import { PrivateRoute } from './utils';
import Onboarding from '../pages/Onboarding/Index';
import Tools from '../pages/Tools';
import Experiments from '../pages/Experiments';
import UsageReporter from '../pages/UsageReporter';
import NewGatedSheet from '../pages/GatedContent/NewGatedSheet';
import { GoogleOAuthProvider } from '@react-oauth/google';

const ONBOARDING_RELEASED: Date = new Date('2022-06-18T00:00');

export default function AppRouter() {
    const [company, setCompany] = useState<Company>();
    const [error, setError] = useState<{ status?: string }>();
    const [noSubscription, setNoSubscription] = useState(false);
    const axios = useAxios();
    const { keycloak } = useKeycloak();
    const [currentPlan, setCurrentPlan] = useState<UserPlan>();
    const [ConnectStripeModal, showConnectStripeModal, handleCloseStripeModal, stripeModalIsOpen] =
        useConnectStripeModal(company);
    const [UpgradeModal, showUpgradePopup] = useUpgradeModal();

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }
        axios.current
            .get<Company>('/company')
            .then((response) => {
                setCompany(response.data);
            })
            .catch((err) => {
                setError(err);
            });
    }, [axios]);

    useEffect(() => {
        if (!company || !keycloak) {
            return;
        }

        const userPlanStrings = Object.values(UserPlan).map((val) => val as string);

        if (process.env.REACT_APP_PRICING_PAGE_ID) {
            const tokenData = keycloak.tokenParsed as any;
            window.pricewell
                ?.features(process.env.REACT_APP_PRICING_PAGE_ID, { email: tokenData.email })
                .then((res) => {
                    if (!res.plan && process.env.REACT_APP_PRICING_PAGE_ID_OLD) {
                        return window.pricewell?.features(process.env.REACT_APP_PRICING_PAGE_ID_OLD, {
                            email: tokenData.email,
                        });
                    } else {
                        return Promise.resolve(res);
                    }
                })
                .then((res) => {
                    if (res?.plan && userPlanStrings.includes(res.plan)) {
                        setCurrentPlan(res?.plan as UserPlan);
                    } else {
                        console.error(`Invalid plan: ${res?.plan}`);
                        setNoSubscription(true);
                    }
                });
        }

        if (!window.$crisp) {
            window.$crisp = [];
            window.$crisp.push(['safe', true]);
            window.CRISP_WEBSITE_ID = '1ba33a87-e3e6-4708-a86c-27cb9748ac08';

            (function () {
                var s = document.createElement('script');

                s.src = 'https://client.crisp.chat/l.js';
                s.async = true;
                document.getElementsByTagName('head')[0].appendChild(s);
            })();
        }

        const tokenData = keycloak.tokenParsed as any;
        window.$crisp.push(['set', 'user:email', tokenData.email || '']);
        window.$crisp.push(['set', 'user:nickname', tokenData.given_name || '']);
        window.$crisp.push(['set', 'session:data', ['sub', tokenData.sub]]);
    }, [company, keycloak]);

    if (error) {
        return (
            <ErrorPage>
                <span className="text-red-500 text-xs">{error.status}</span>
            </ErrorPage>
        );
    } else if (!company) {
        return <Loader full />;
    } else {
        window.heap.addUserProperties({ company_id: company.id });
    }

    const showOnboarding = !company?.onboarding?.completedAt && new Date(company.createdAt) > ONBOARDING_RELEASED;

    return (
        <ToastProvider autoDismiss>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_SHEETS_CLIENT_ID || ''}>
                <CurrentCompanyContext.Provider value={company}>
                    <CurrentPlanContext.Provider value={currentPlan}>
                        {noSubscription && <NoSubscriptionPage />}
                        {!noSubscription && (
                            <>
                                <Sidebar currentPlan={currentPlan} showUpgradePopup={showUpgradePopup} />
                                <PageWrapper>
                                    <PageHeader
                                        changelog
                                        currentPlan={currentPlan}
                                        showUpgradePopup={showUpgradePopup}
                                    />
                                    {!!company && !company?.stripe?.stripeId && (
                                        <div>
                                            <div className="text-center flex flex-col p-4 mt-20 sm:mt-0 md:text-left md:flex-row md:items-center md:justify-between md:p-12 bg-blue-100 rounded-md shadow-lg">
                                                <div className="text-2xl font-semibold">
                                                    <div className="text-gray-900">Welcome to PriceWell 🎉</div>
                                                    <div className="text-blue-500">
                                                        Connect Stripe to get the full experience
                                                    </div>
                                                </div>

                                                <div className="mt-3 md:mt-0 md:ml-2">
                                                    <ConnectStripeButton onClick={showConnectStripeModal} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex flex-1 overflow-y-auto pt-16 md:pt-0">
                                        <ErrorBoundary>
                                            <>
                                                <Switch>
                                                    <PrivateRoute
                                                        exact
                                                        path={ROUTES.DASHBOARD}
                                                        component={showOnboarding ? Onboarding : HomePage}
                                                    />
                                                    <PrivateRoute
                                                        exact
                                                        path={ROUTES.CONNECT_STRIPE}
                                                        component={HomePage}
                                                    />
                                                    <PrivateRoute exact path={ROUTES.PAGES} component={PricingPages} />
                                                    <PrivateRoute path={ROUTES.PAGES_NEW} component={NewPage} />
                                                    <PrivateRoute
                                                        exact
                                                        path={ROUTES.CUSTOMER_PORTALS}
                                                        component={CustomerPortals}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.CUSTOMER_PORTALS_NEW}
                                                        component={NewCustomerPortal}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.CUSTOMER_PORTALS_CONFIG}
                                                        component={NewCustomerPortal}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.CUSTOMER_PORTALS_SNIPPET}
                                                        component={NewCustomerPortal}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.FEATURE_FLAGS_CONFIGURE}
                                                        component={ConfigureFeatureFlags}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.FEATURE_FLAGS}
                                                        component={FeatureFlags}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT}
                                                        exact
                                                        component={GatedContent}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_SHEETS}
                                                        exact
                                                        component={NewGatedSheet}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_SHEETS_EDIT}
                                                        exact
                                                        component={NewGatedSheet}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_SHEETS_SNIPPET}
                                                        exact
                                                        component={NewGatedSheet}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_NEW}
                                                        exact
                                                        component={NewGatedContent}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_EDIT}
                                                        exact
                                                        component={NewGatedContent}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.GATED_CONTENT_SNIPPET}
                                                        exact
                                                        component={NewGatedContent}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute
                                                        path={ROUTES.INTEGRATIONS}
                                                        component={Integrations}
                                                        requiredPlan={PAID_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute exact path={ROUTES.TOOLS} component={Tools} />
                                                    <PrivateRoute
                                                        path={ROUTES.EXPERIMENTS}
                                                        component={Experiments}
                                                        requiredPlan={PRO_PLANS}
                                                        currentPlan={currentPlan}
                                                    />
                                                    <PrivateRoute exact path={ROUTES.HELP} component={HelpPage} />
                                                    <PrivateRoute exact path={ROUTES.COUPONS} component={Coupons} />
                                                    <PrivateRoute
                                                        exact
                                                        path={ROUTES.INVOICES}
                                                        component={InvoiceGenerator}
                                                    />
                                                    <PrivateRoute exact path={ROUTES.BILLING} component={Billing} />
                                                    <PrivateRoute exact path={ROUTES.SETTINGS} component={Settings} />
                                                    <PrivateRoute
                                                        exact
                                                        path={ROUTES.USAGE_RECORDER}
                                                        requiredPlan={PRO_PLANS}
                                                        currentPlan={currentPlan}
                                                        component={UsageReporter}
                                                    />
                                                    <PrivateRoute component={NotFound} />
                                                </Switch>
                                                <ConnectStripeModal
                                                    isOpen={stripeModalIsOpen}
                                                    company={company}
                                                    onClose={handleCloseStripeModal}
                                                />
                                                <UpgradeModal />
                                            </>
                                        </ErrorBoundary>
                                    </div>
                                </PageWrapper>
                            </>
                        )}
                    </CurrentPlanContext.Provider>
                </CurrentCompanyContext.Provider>
            </GoogleOAuthProvider>
        </ToastProvider>
    );
}
